import { useCallback, useMemo, useState, type CSSProperties, type ReactNode, type ThHTMLAttributes } from 'react';

export type SortDirection = 'asc' | 'desc';
export type SortState<Key extends string = string> = { key: Key; direction: SortDirection } | null;
export type SortValue = string | number | Date | null | undefined;

const emptyLast = (value: SortValue) =>
  value == null ||
  (typeof value === 'string' && (value.trim() === '' || value.trim() === '-')) ||
  (typeof value === 'number' && !Number.isFinite(value)) ||
  (value instanceof Date && !Number.isFinite(value.getTime()));

/** Compare raw row values rather than formatted table text so percentages and
 * localized numbers always sort as numbers. Empty/invalid values stay last. */
export function compareSortValues(left: SortValue, right: SortValue, direction: SortDirection = 'asc'): number {
  const leftEmpty = emptyLast(left);
  const rightEmpty = emptyLast(right);
  if (leftEmpty || rightEmpty) {
    if (leftEmpty && rightEmpty) return 0;
    return leftEmpty ? 1 : -1;
  }
  let result: number;
  if (left instanceof Date || right instanceof Date) {
    const leftTime = left instanceof Date ? left.getTime() : new Date(left as string | number).getTime();
    const rightTime = right instanceof Date ? right.getTime() : new Date(right as string | number).getTime();
    result = leftTime - rightTime;
  } else if (typeof left === 'number' && typeof right === 'number') {
    result = left - right;
  } else {
    result = String(left).localeCompare(String(right), 'zh-CN', { numeric: true, sensitivity: 'base' });
  }
  return direction === 'asc' ? result : -result;
}

export function sortRows<T, Key extends string>(
  rows: readonly T[],
  sort: SortState<Key>,
  valueFor: (row: T, key: Key) => SortValue,
): T[] {
  if (!sort) return [...rows];
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const result = compareSortValues(valueFor(left.row, sort.key), valueFor(right.row, sort.key), sort.direction);
      return result === 0 ? left.index - right.index : result;
    })
    .map(item => item.row);
}

/**
 * Sort a hierarchy without flattening it: every set of siblings is ordered by
 * the selected field while the parent/child relationship and expansion state
 * remain intact. Pinned rows (for example a subtotal) always remain first in
 * their own sibling set.
 */
export function sortTreeRows<T, Key extends string>(
  rows: readonly T[],
  sort: SortState<Key>,
  valueFor: (row: T, key: Key) => SortValue,
  childrenFor: (row: T) => readonly T[],
  withChildren: (row: T, children: T[]) => T,
  isPinned: (row: T) => boolean = () => false,
): T[] {
  const sortSiblings = (siblings: readonly T[]): T[] => {
    const pinned = siblings.filter(isPinned);
    const normal = siblings.filter(row => !isPinned(row));
    const ordered = sort ? sortRows(normal, sort, valueFor) : [...normal];
    return [...pinned, ...ordered].map(row => {
      const children = sortSiblings(childrenFor(row));
      return withChildren(row, children);
    });
  };
  return sortSiblings(rows);
}

/**
 * Common client-side sorting state for dashboard tables. A header cycles
 * ascending → descending → source order. Source order is restored when the
 * user completes the cycle.
 */
export function useTableSort<T, Key extends string>(
  rows: readonly T[],
  valueFor: (row: T, key: Key) => SortValue,
  initialSort: SortState<Key> = null,
) {
  const [sort, setSort] = useState<SortState<Key>>(initialSort);
  const toggleSort = useCallback((key: Key) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);
  const sortedRows = useMemo(() => sortRows(rows, sort, valueFor), [rows, sort, valueFor]);
  return { sort, toggleSort, sortedRows, setSort };
}

type SortableHeaderProps<Key extends string> = Omit<ThHTMLAttributes<HTMLTableCellElement>, 'children' | 'onClick'> & {
  sortKey: Key;
  sort: SortState<Key>;
  onSort: (key: Key) => void;
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  disabled?: boolean;
};

export function SortableHeader<Key extends string>({
  sortKey,
  sort,
  onSort,
  children,
  align = 'left',
  disabled = false,
  style,
  ...rest
}: SortableHeaderProps<Key>) {
  const active = sort?.key === sortKey;
  const direction = active ? sort.direction : undefined;
  const mergedStyle: CSSProperties = { ...style, textAlign: align };
  return (
    <th {...rest} style={mergedStyle} aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}>
      <button
        type="button"
        className={`sortable-table-header${active ? ' is-active' : ''}`}
        onClick={() => onSort(sortKey)}
        disabled={disabled}
        title={disabled ? '无权限按此字段排序' : direction === 'asc' ? '当前升序，点击切换为降序' : direction === 'desc' ? '当前降序，点击恢复原始顺序' : '点击按此字段升序排列'}
      >
        <span>{children}</span>
        {!disabled && <span className="sortable-table-indicator" aria-hidden="true">{direction === 'asc' ? '▲' : direction === 'desc' ? '▼' : '↕'}</span>}
      </button>
    </th>
  );
}
